import { ChevronRight, Ellipsis, LogOut } from 'lucide-react';
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useT } from '@/shared/i18n/useT';
import { cn } from '@/shared/utils/cn';
import { Icon } from '../icons/Icon';
import { Sheet } from '../ui/Sheet';
import { NavIcon } from './NavIcon';
import { BOTTOM_NAV_IDS, isNavActive, type NavGroup, type NavItem } from './navModel';

export interface BottomNavProps {
  groups: NavGroup[];
  /** Notificaciones sin leer: punto en «Más» y contador en «Notificaciones». */
  unread?: number;
  /** Si hay sesión, «Salir» aparece al final de la hoja «Más» (en móvil ya no está en la barra superior). */
  onSignOut?: () => void;
}

const itemBase =
  'relative flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 pt-1.5 text-xs font-medium text-fg-muted ' +
  'transition-colors duration-fast ease-standard';

/** Barra inferior móvil: Inicio · Precios · Calculadora · Alertas · Más (hoja con el resto de secciones). */
export function BottomNav({ groups, unread = 0, onSignOut }: BottomNavProps) {
  const { t } = useT();
  const { pathname } = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);

  const all = groups.flatMap((group) => group.items);
  const primary = BOTTOM_NAV_IDS.map((id) => all.find((item) => item.id === id)).filter(
    (item): item is NavItem => Boolean(item),
  );
  const rest = groups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => !BOTTOM_NAV_IDS.includes(item.id)),
    }))
    .filter((group) => group.items.length > 0);
  const moreActive = rest.some((group) => group.items.some((item) => isNavActive(pathname, item)));

  return (
    <nav
      aria-label={t('nav.bottom')}
      className="pb-safe fixed inset-x-0 bottom-0 z-40 border-t border-border-soft bg-card/95 backdrop-blur lg:hidden"
    >
      <ul className="mx-auto flex h-[var(--nav-h)] max-w-xl items-stretch px-1">
        {primary.map((item) => {
          const active = isNavActive(pathname, item);
          return (
            <li key={item.id} className="flex min-w-0 flex-1">
              <Link
                to={item.to}
                aria-current={active ? 'page' : undefined}
                className={cn(itemBase, active && 'font-semibold text-primary-strong')}
              >
                <span
                  className={cn(
                    'grid h-8 w-14 place-items-center rounded-full transition-colors duration-base ease-standard',
                    active && 'bg-primary-tint',
                  )}
                >
                  <NavIcon icon={item.icon} size={24} />
                </span>
                <span className="max-w-full truncate px-1">{t(item.label)}</span>
              </Link>
            </li>
          );
        })}
        <li className="flex min-w-0 flex-1">
          <Sheet
            open={moreOpen}
            onOpenChange={setMoreOpen}
            side="bottom"
            title={t('nav.moreTitle')}
            trigger={
              <button
                type="button"
                data-active={moreActive ? 'true' : undefined}
                className={cn(itemBase, moreActive && 'font-semibold text-primary-strong')}
              >
                <span
                  className={cn(
                    'relative grid h-8 w-14 place-items-center rounded-full transition-colors duration-base ease-standard',
                    moreActive && 'bg-primary-tint',
                  )}
                >
                  <Icon icon={Ellipsis} size={24} />
                  {unread > 0 ? (
                    <span
                      aria-hidden="true"
                      data-testid="more-unread-dot"
                      className="absolute right-3 top-1.5 size-2.5 rounded-full bg-accent ring-2 ring-card"
                    />
                  ) : null}
                </span>
                <span className="max-w-full truncate px-1">{t('nav.more')}</span>
              </button>
            }
          >
            <div className="flex flex-col gap-4 pb-2">
              {rest.map((group) => (
                <div key={group.id}>
                  <p
                    id={`more-group-${group.id}`}
                    className="pb-1 text-xs font-semibold uppercase tracking-wider text-fg-muted"
                  >
                    {t(group.label)}
                  </p>
                  <ul aria-labelledby={`more-group-${group.id}`} className="flex flex-col">
                    {group.items.map((item) => {
                      const active = isNavActive(pathname, item);
                      return (
                        <li key={item.id}>
                          <Link
                            to={item.to}
                            aria-current={active ? 'page' : undefined}
                            onClick={() => setMoreOpen(false)}
                            className={cn(
                              'flex h-14 items-center gap-3 rounded-md px-2 text-base text-fg transition-colors duration-fast ease-standard hover:bg-muted',
                              active && 'font-semibold text-primary-strong',
                            )}
                          >
                            <span
                              className={cn(
                                'grid size-10 place-items-center rounded-md bg-muted text-fg-muted',
                                active && 'bg-primary-tint text-primary-strong',
                              )}
                            >
                              <NavIcon icon={item.icon} />
                            </span>
                            <span className="min-w-0 flex-1 truncate">{t(item.label)}</span>
                            {item.id === 'notificaciones' && unread > 0 ? (
                              <span
                                aria-label={t('aria.notificationsUnread', { count: unread })}
                                className="num grid h-[22px] min-w-[22px] place-items-center rounded-full bg-accent px-1.5 text-xs font-bold text-accent-fg"
                              >
                                {unread > 9 ? '9+' : unread}
                              </span>
                            ) : null}
                            <Icon icon={ChevronRight} size={16} className="text-fg-muted" />
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
              {onSignOut ? (
                <button
                  type="button"
                  onClick={() => {
                    setMoreOpen(false);
                    onSignOut();
                  }}
                  className="flex h-14 items-center gap-3 rounded-md border-t border-border-soft px-2 text-base text-fg transition-colors duration-fast ease-standard hover:bg-muted"
                >
                  <span className="grid size-10 place-items-center rounded-md bg-muted text-fg-muted">
                    <Icon icon={LogOut} />
                  </span>
                  <span className="min-w-0 flex-1 truncate text-left">{t('actions.signOut')}</span>
                </button>
              ) : null}
            </div>
          </Sheet>
        </li>
      </ul>
    </nav>
  );
}
